<template>
  <div :id="mapWrapperId" class="map-wrapper" dir="ltr">
    <!-- We are setting the direction to ltr, regardless of the direction of the rest of the content,
         to ensure that maplibre's CSS around positioning and popups works correctly. -->
    <slot :map="map" />
  </div>
</template>

<script>
import { GeolocateControl, Map, NavigationControl, setRTLTextPlugin } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'

const DEFAULT_GEOAPIFY_STYLE = 'https://api-eu.geoapify.com/v1/styles/klokantech-basic/style.json'

export default {
  props: {
    bounds: {
      type: Array,
      required: true
    },
    center: {
      type: Object,
      required: false,
      default: () => { return  { lat: 0, lng: 0 } }
    },
    initialZoom: {
      type: Number,
      required: false
    },
    mapWrapperId: {
      type: String,
      required: false,
      default: 'map'
    },
    maxZoom: {
      type: Number,
      required: false
    },
    navigationOptions: {
      type: Object,
      required: false,
      default: () => ({})
    },
    showGeolocateControl: {
      type: Boolean,
      required: false,
      default: false
    },
    style: {
      type: String,
      required: false
    }
  },

  emits: ['loaded'],

  data() {
    return {
      map: null
    }
  },

  computed: {
    defaultStyle() {
      switch (this.agraConfiguration.mapTilesProvider.kind) {
      case 'aws_location_service':
        return `https://maps.geo.${this.agraConfiguration.awsRegion}.amazonaws.com/maps/v0/maps/esri_navigation/style-descriptor`
      case 'geoapify':
        return DEFAULT_GEOAPIFY_STYLE
      default:
        return null
      }
    },

    mapOptions() {
      return {
        bounds: this.bounds,
        center: this.center,
        container: 'map',
        maxZoom: this.maxZoom,
        style: this.style || this.defaultStyle,
        transformRequest: this.mapStyleUrlTransformFunction,
        zoom: this.initialZoom || this.maxZoom || 0
      }
    },

    mapStyleUrlTransformFunction() {
      switch (this.agraConfiguration.mapTilesProvider.kind) {
      case 'aws_location_service': {
        const apiKey = this.agraConfiguration.mapTilesProvider.clientApiToken
        return function(url) {
          const urlObj = new URL(url)
          urlObj.searchParams.set('key', apiKey)
          return { url: urlObj.toString() }
        }
      }
      case 'geoapify': {
        // Use this method to plug the API key into the style URL
        const apiKey = this.agraConfiguration.mapTilesProvider.clientApiToken
        return function(url) {
          const urlObj = new URL(url)
          urlObj.searchParams.set('apiKey', apiKey)
          return { url: urlObj.toString() }
        }
      }
      default:
        return function(url) {
          return { url }
        }
      }
    }
  },

  mounted() {
    this.map = new Map(this.mapOptions)

    let nav = new NavigationControl(this.navigationOptions)
    this.map.addControl(nav, 'top-right')

    if (this.showGeolocateControl) {
      let geolocate = new GeolocateControl()
      this.map.addControl(geolocate, 'top-right')
    }

    this.map.on('load', () => {
      this.setMapLanguage()
      this.$emit('loaded', {map: this.map})
    })
  },

  methods: {
    setMapLanguage() {
      if (this.i18n.isRtl()) {
        // Load the plugin so that RTL text labels on the map are displayed in the right direction.
        // This is the approach recommended in https://maplibre.org/maplibre-gl-js/docs/examples/mapbox-gl-rtl-text/
        // Q: Could we install https://github.com/mapbox/mapbox-gl-rtl-text instead of passing a string here?
        // A: Sadly, it seems setRTLTextPlugin can only handle a URL; there doesn't seem to be any way to hook it up
        //    to a yarn package.
        setRTLTextPlugin('https://unpkg.com/@mapbox/mapbox-gl-rtl-text@0.2.3/mapbox-gl-rtl-text.js', false)
      }

      const desiredLanguage = this.i18n.locale().split('-')[0] // if locale is e.g., de-CH, we ignore '-CH'

      // For both map providers, we're going to set properties on relevant map layers to specify
      // what language should be used.
      let layers = this.map.getStyle().layers

      // AWS Location Service and Geoapify have different syntax for specifying the language on a layer.
      switch (this.agraConfiguration.mapTilesProvider.kind) {
      case 'aws_location_service': {
        const layoutLayers = layers.filter((layer) => layer.layout && layer.layout['text-field'] && ['{_name_global}', '{_name}'].includes(layer.layout['text-field']))
        for (let layer of layoutLayers) {
          this.map.setLayoutProperty(layer.id, 'text-field', ['case', ['has', `_name_${desiredLanguage}`],
                                                              ['get', `_name_${desiredLanguage}`],
                                                              ['get', '_name']])
        }
        return
      }
      case 'geoapify': {
        let labelLayers = layers.filter((layer) => layer.type === 'symbol')
        for (let layer of labelLayers) {
          // See https://www.geoapify.com/map-language-localize-labels-on-the-map for discussion of this syntax.
          this.map.setLayoutProperty(layer.id, 'text-field', ['case', ['has', `name:${desiredLanguage}`],
                                                              ['get', `name:${desiredLanguage}`],
                                                              ['get', 'name:latin']])
        }
        return
      }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.map-wrapper {
  height: 100%;
  width: 100%;
}
</style>
